import { CircularProgress, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { ApplicationAnswerProps } from '@/models/Application';
import { ProgramNames } from '@/models/Program';
import { QuestionProps } from '@/models/Question';
import { StandardQuestionDetails } from '@/models/Standard';
import { logError } from '@/utils/errorHandlerUtil';

const TotalExpensePerCapita = ({ question }: { question: QuestionProps }) => {
  const theme = useTheme();

  const [currentTotalPerCapita, setCurrentTotalPerCapita] = useState(0);
  const { selectedProgramId } = useContext(SelectedProgramContext);

  const {
    canEdit,
    answers,
    questions,
    currentStandardId,
    currentApplication,
    makingApiRequest,
    updateMakingApiRequest,
    saveApplicationAnswer,
  } = useContext(CurrentApplicationContext);

  const questionAnswer = useMemo(() => {
    return answers[question.id];
  }, [answers, question.id]);

  const questionsInStandard = useMemo(
    () =>
      questions.standards.find((st: StandardQuestionDetails) => st.standardId == currentStandardId)
        ?.questions ?? [],
    [questions, currentStandardId]
  );

  const includedInTotalQuestionSum = useMemo(() => {
    if (!answers || !questionsInStandard) return 0;

    // find sum of all includedInTotal questions
    const includedInTotal = Object.values<ApplicationAnswerProps>(answers)?.filter(
      (answer: ApplicationAnswerProps) =>
        answer.includeInTotal != null &&
        questionsInStandard.some((q: QuestionProps) => q.id == answer.questionId)
    );

    const total = includedInTotal?.reduce(function (acc: any, ob: any) {
      return acc + ob.responseInt + ob.responseDecimal;
    }, 0);

    return total;
  }, [answers, questionsInStandard]);

  // this will no longer work if we introduce a second question type that is hours on the current standard
  const volunteerHoursQuestionAnswer = useMemo(
    () =>
      Object.values<ApplicationAnswerProps>(answers).find(
        (q: ApplicationAnswerProps) =>
          q.questionType == 'hours' &&
          questionsInStandard.some((qu: QuestionProps) => qu.id == q.questionId)
      ),
    [answers, questionsInStandard]
  );

  const population: number | null = useMemo(
    () =>
      Object.values<ApplicationAnswerProps>(answers).find(
        (q: ApplicationAnswerProps) =>
          q.questionType == 'population' &&
          questionsInStandard.some((qu: QuestionProps) => qu.id == q.questionId)
      )?.responseInt ?? null,
    [answers, questionsInStandard]
  );

  useEffect(() => {
    if (!population) return;

    if (volunteerHoursQuestionAnswer && question.hourlyVolunteerRate == null) {
      logError(`Hourly volunteer rate is null for question ${question.id}.`);
    }

    const totalExpenditures =
      includedInTotalQuestionSum +
      (volunteerHoursQuestionAnswer?.responseInt ?? 0) * (question.hourlyVolunteerRate ?? 1);

    let totalExpensePerCapita = totalExpenditures / population;

    totalExpensePerCapita = Math.round((totalExpensePerCapita + Number.EPSILON) * 100) / 100;

    setCurrentTotalPerCapita(totalExpensePerCapita);
  }, [
    includedInTotalQuestionSum,
    population,
    question.hourlyVolunteerRate,
    question.id,
    volunteerHoursQuestionAnswer,
  ]);

  const [loading, setLoading] = useState(false);

  const [savingAnswer, setSavingAnswer] = useState(false);

  useEffect(() => {
    const threshold = selectedProgramId == ProgramNames.TreeCityUSA ? 2 : 0.1;

    if (
      savingAnswer &&
      questionAnswer &&
      (currentTotalPerCapita == questionAnswer.responseDecimal ||
        currentTotalPerCapita == questionAnswer.responseInt ||
        (currentTotalPerCapita < threshold && questionAnswer.responseDecimal == null))
    ) {
      setSavingAnswer(false);
    }
  }, [savingAnswer, currentTotalPerCapita, questionAnswer, selectedProgramId]);

  useEffect(() => {
    if (!canEdit) return;

    const threshold = selectedProgramId == ProgramNames.TreeCityUSA ? 2 : 0.1;

    if (!savingAnswer && questionAnswer && currentApplication) {
      if (
        currentTotalPerCapita &&
        !(
          currentTotalPerCapita == questionAnswer.responseDecimal ||
          currentTotalPerCapita == questionAnswer.responseInt
        ) &&
        currentTotalPerCapita >= threshold
      ) {
        setSavingAnswer(true);
        saveApplicationAnswer(
          currentApplication.id,
          question.id,
          currentTotalPerCapita,
          'decimal'
        ).then(() => {
          updateMakingApiRequest(false);
        });
      } else if (currentTotalPerCapita < threshold && questionAnswer.responseDecimal != null) {
        setSavingAnswer(true);
        saveApplicationAnswer(currentApplication.id, question.id, null, 'decimal').then(() => {
          updateMakingApiRequest(false);
        });
      }
    }
  }, [
    currentApplication,
    currentTotalPerCapita,
    question.id,
    questionAnswer,
    selectedProgramId,
    saveApplicationAnswer,
    updateMakingApiRequest,
    canEdit,
    savingAnswer,
  ]);

  useEffect(() => {
    setLoading(makingApiRequest);
  }, [makingApiRequest]);

  return (
    <div style={{ marginBottom: '20px' }}>
      <Typography variant='h3' style={{ marginTop: '20px', marginBottom: '10px' }}>
        {question.questionText}
      </Typography>
      <div>
        {currentTotalPerCapita >= 0 && (
          <div
            style={{
              color:
                (selectedProgramId == ProgramNames.TreeCityUSA && currentTotalPerCapita < 2) ||
                (selectedProgramId == ProgramNames.TreeLineUSA && currentTotalPerCapita < 0.1)
                  ? theme.palette.error.main
                  : theme.palette.forest.main,
              width: 'auto',
              display: 'inline-block',
            }}
          >
            $
            {currentTotalPerCapita
              ? currentTotalPerCapita.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : '0.00'}
          </div>
        )}
        {loading && (
          <div
            style={{
              display: 'inline-block',
              position: 'relative',
              top: '3px',
              width: '20px',
              height: '20px',
              marginLeft: '10px',
            }}
          >
            <CircularProgress size='16px' />
          </div>
        )}
      </div>

      {(selectedProgramId == ProgramNames.TreeCityUSA && currentTotalPerCapita < 2) ||
      (selectedProgramId == ProgramNames.TreeLineUSA &&
        currentTotalPerCapita < 0.1 &&
        population != null &&
        population > 0) ? (
        <div style={{ minWidth: '500px', maxWidth: '500px', marginTop: '5px' }}>
          <Typography
            style={{ minWidth: '500px', maxWidth: '500px' }}
            marginLeft={1}
            variant='subtitle3'
            color='error'
          >
            {question?.questionNote}
          </Typography>
        </div>
      ) : (
        <div />
      )}
    </div>
  );
};

export default TotalExpensePerCapita;
